import { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { getTableSchema } from './TableSchema';
import endpoints from '@app/constants/endpoints';
import {
  AlloyButton,
  InlineLoader,
  Table,
  StickySection,
  ComponentLoader,
  ToastContext,
  HeightLimitedExpander
} from 'cat-ecommerce-alloy';
import styles from './CustomizeModalTable.module.scss';
import { useSelector, useDispatch } from 'react-redux';
import cx from 'classnames';
import { AvailabilityInformation, QuantityInformation } from './CustomRenders';
import {
  STATUS,
  PAGE_BREAKPOINTS,
  BUNDLE_BEAN,
  PACKAGE_BEAN
} from '@app/constants/commonConstants';
import { getProp65Information } from './getProp65Information';
import {
  useBreakpoint,
  useHttp,
  usePriceAndAvailability,
  useFitmentCheck,
  useAnalytics
} from '@app/hooks';
import { normalizeError } from '@app/store/exception/utils';
import { clearError, setError } from '@app/store/exception/actions';
import { AddToCartToast } from '@app/components/common/product/ProductToast';
import { ERROR_DOMAIN, ERROR_PATH } from '@app/constants/errorConstants';
import ProductLink from '../../../ProductLink';
import { replaceTokensInUrl, setCookie } from '@app/utils';
import CartIcon from './CartIcon';
import { MAX_QUANTITY } from '@app/store/products/constants';
import debounce from 'lodash/debounce';
import {
  PCC_PDP_BUNDLE_KITS_LIST,
  PCC_PDP_KITS_LIST
} from '@app/constants/analyticsConstants';
import { convertFormattedNumberToUnformatted } from '@app/components/pages/checkout/utils';

const CustomizeModalTable = ({
  items,
  closeModal,
  addItemsToCart,
  loadingAddItemsToCart,
  gaListName
}) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const currencyCode = useSelector(s => s.dealerCustomer?.currency) ?? '';
  const { getFitmentCheck } = useFitmentCheck();

  const itemsIncludedSchema = getTableSchema(t, currencyCode);
  const isInstantAccess = useSelector(s => s.common?.isInstantAccess);

  const priceAndAvailability = useSelector(
    s => s.products?.priceAndAvailability
  );

  const current = useSelector(s => s.products?.current);
  const beanType = useSelector(s => s.products.byId?.[current]?.type);
  const isPackage = beanType === PACKAGE_BEAN;
  const productListName = isPackage
    ? PCC_PDP_KITS_LIST
    : PCC_PDP_BUNDLE_KITS_LIST;

  const productItems = useSelector(
    s => s.products?.byId?.[current]?.components
  );
  const currentKit = useSelector(s => s.products?.byId?.[current]);
  const currentKitPartNumber = currentKit.partNumber;
  const model = useSelector(
    s => s.myEquipment?.equipments?.selectedEquipment?.model
  );
  const formattedZeroPrice = useSelector(
    s => s.dealerCustomer?.formattedZeroPrice
  );
  const langId = useSelector(state => state?.common?.langId);
  const ordersCurrent = useSelector(s => s.orders?.current);
  const orderItems =
    useSelector(s => s.orders?.byId?.[ordersCurrent]?.orderItem) ?? [];

  const sosCode = useSelector(
    s => s.products?.priceAndAvailability?.[currentKitPartNumber]?.sosCode
  );

  const storeId = useSelector(state => state?.common?.storeId);
  const dealerStore = useSelector(s => s.common?.storeLocationName) ?? '';
  const isSM = useBreakpoint(PAGE_BREAKPOINTS.SM);

  const ctx = useContext(ToastContext);

  const { invoke, loading: subtotalLoading } = useHttp();
  const { invoke: invokePA, status: statusPA } = usePriceAndAvailability();

  const [itemsAreLoading, setItemsAreLoading] = useState(false);
  const [showSubTotalLoader, setShowSubTotalLoader] = useState(false);
  const [itemsHaveInvalidQuantity, setItemsHaveInvalidQuantity] =
    useState(false);
  const [itemsDisabledButtonList, setItemsDisabledButtonList] = useState([]);
  const [debouncedItems, setDebouncedItems] = useState([]);
  const [debouncedItemsResolved, setDebouncedItemsResolved] = useState(true);
  const [subTotal, setSubTotal] = useState('');
  const [isSubtotalMounted, setIsSubtotalMounted] = useState(false);
  const { fireProductClickEvent, fireAddToCartEvent } = useAnalytics();

  const disableAddSelectionToCart =
    loadingAddItemsToCart ||
    itemsAreLoading ||
    itemsHaveInvalidQuantity ||
    itemsDisabledButtonList.some(item => isNaN(item.quantity)) ||
    itemsDisabledButtonList.length === items.length ||
    subTotal === formattedZeroPrice;

  const subTotalCallBack = useCallback(async () => {
    const url = replaceTokensInUrl(endpoints.TOTAL_PRICE__URL, storeId);
    // filter the items that doesn't have price before send it to the subtotal API
    const lineItemsPrice = items
      .filter(item => item.unformattedTotalPrice !== '')
      .map(item => item.unformattedTotalPrice);
    return invoke({
      url: url,
      method: 'post',
      rethrowError: true,
      data: { lineItemsPrice, langId }
    });
  }, [storeId, invoke, items]);

  const setSubtotalHandler = useCallback(async () => {
    if (isInstantAccess) {
      const subtotal = await subTotalCallBack();
      setSubTotal(subtotal?.formattedTotal);
    }
  }, [storeId, items, isInstantAccess]);

  useEffect(() => {
    /**
     * call the subtotal API first time
     * and inform to the Quantity component that
     * the subtotal renders for the first time.
     */
    setSubtotalHandler();
    setIsSubtotalMounted(true);
  }, []);

  // * This handles the debounce for calling the price and
  // * availability when items are still changing quantity
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedPA = useCallback(
    debounce(debouncedItems => {
      invokePA({ parts: debouncedItems });
      setDebouncedItems([]);
      setDebouncedItemsResolved(true);
    }, 1000),
    []
  );

  // * This waits for all the items to be changed before calling
  // * the price and availability
  useEffect(() => {
    if (debouncedItems.length && statusPA !== STATUS.PENDING) {
      setItemsAreLoading(true);
      setDebouncedItemsResolved(false);
      debouncedPA(debouncedItems);
    }
  }, [debouncedItems, statusPA]);

  // * This calls the subtotal when every items has been resolved
  useEffect(() => {
    if (!itemsAreLoading && debouncedItemsResolved && !debouncedItems.length) {
      // * items quantities are always the original, so we use
      // * priceAndAvailability quantities instead
      setItemsDisabledButtonList(prev =>
        prev.filter(item => {
          const quantity = !isNaN(item.quantity) && item.quantity;

          // * Filter all valid quantities, except 0 and above 99999
          return quantity && quantity <= MAX_QUANTITY ? false : true;
        })
      );
      setSubtotalHandler();
    }
  }, [itemsAreLoading, debouncedItemsResolved, debouncedItems]);

  useEffect(() => {
    // * Disable add-selection-to-cart if any item is still pending
    setItemsAreLoading(
      items.some(item => item?.loadingStatus === STATUS.PENDING)
    );

    // * If any item has invalid quantity (above 99999)
    // * Loader will show for 1s and then show price-unavailable label
    // * Add selection to cart will be disabled
    const isInvalid = items.some(item => {
      const priceAndAvailabilityItem = priceAndAvailability?.[item.key];

      // * Whenever quantity is changed, priceAndAvailability quantity
      // * is available to use since items.quantity doesn't change.
      if (priceAndAvailabilityItem?.quantity > MAX_QUANTITY) {
        return true;
      } else if (
        priceAndAvailabilityItem?.quantity === undefined &&
        item.quantity > MAX_QUANTITY
      ) {
        return true;
      } else {
        return false;
      }
    });

    setShowSubTotalLoader(isInvalid);
    setItemsHaveInvalidQuantity(isInvalid);
    const timeout = setTimeout(() => {
      setShowSubTotalLoader(false);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [items, priceAndAvailability]);

  const itemImageRenderer = ({ thumbnail }) => {
    return (
      <div>
        <img loading="lazy" width="96px" height="96px" src={thumbnail} />
      </div>
    );
  };

  const addToCartRenderer = ({ price, partNumber, loadingStatus }) => {
    const isLoading = loadingStatus === STATUS.PENDING;
    // if the price is empty or is 0 we disabled the cart button
    const disabledAddToCart =
      price && price !== formattedZeroPrice ? false : true;
    const isInDisabledList = itemsDisabledButtonList.some(
      item => item.partNumber === partNumber
    );
    return (
      <AlloyButton
        buttonType="secondary"
        disabled={isLoading || isInDisabledList || disabledAddToCart}
        onClick={() => addToCartHandler(partNumber)}
      >
        {t('ADD_TO_CART')}
      </AlloyButton>
    );
  };

  const getItemsInCartRenderer = ({ partNumber }) => {
    const quantityInCart = orderItems?.reduce((total, item) => {
      if (item.partNumber === partNumber) {
        return total + +item.quantity;
      } else {
        return total;
      }
    }, 0);
    return <CartIcon quantityInCart={quantityInCart} isSM={isSM} />;
  };

  const getPriceInformation = ({
    partNumber,
    price,
    loadingStatus,
    quantity
  }) => {
    // * Shows price unavailable if item quantity is above limit (99999)
    const availabilityQuantity = priceAndAvailability?.[partNumber]?.quantity;
    const hasValidQuantity =
      availabilityQuantity <= MAX_QUANTITY || quantity <= MAX_QUANTITY;
    const isLoading = loadingStatus === STATUS.PENDING;
    let priceInformationText = price ? price : t('PD_PRICE_UNAVAILABLE');
    priceInformationText =
      priceInformationText !== formattedZeroPrice ? priceInformationText : '';
    return isLoading ? (
      <InlineLoader />
    ) : (
      <span
        className={cx({
          'text-univers--bold u-fs--16': hasValidQuantity && price
        })}
      >
        {hasValidQuantity && priceInformationText}
      </span>
    );
  };

  const getQuantityRenderer = item => {
    return (
      <QuantityInformation
        productInfo={item}
        setItemsDisabledButtonList={setItemsDisabledButtonList}
        setDebouncedItems={setDebouncedItems}
        setSubtotalHandler={setSubtotalHandler}
        isSubtotalMounted={isSubtotalMounted}
      />
    );
  };

  const getAvailabilityInformation = item => {
    // * Hide if item quantity is above limit (99999)
    const availabilityQuantity =
      priceAndAvailability?.[item.partNumber]?.quantity;
    const hasValidQuantity =
      availabilityQuantity <= MAX_QUANTITY || item.quantity <= MAX_QUANTITY;

    // TODO: We need something to display here instead of null
    return hasValidQuantity ? (
      <AvailabilityInformation partNumber={item.partNumber} {...item} />
    ) : null;
  };

  const getProductInformation = item => {
    const {
      name,
      partNumber,
      productDisplayUrl,
      shortDescription,
      prop65,
      key,
      loadingStatus,
      price,
      unformattedTotalPrice
    } = item;
    const kit = { ...item, price: unformattedTotalPrice };
    const isLoading = loadingStatus === STATUS.PENDING;
    // if the price is empty or is 0 we disabled the cart button
    const disabledAddToCart =
      price && price !== formattedZeroPrice ? false : true;
    const isInDisabledList = itemsDisabledButtonList.some(
      item => item.uniqueId === key
    );
    return (
      <>
        <ProductLink
          link={productDisplayUrl}
          key={`${key}-product-link`}
          className="u-fs--16 text-univers"
          productClickDetails={kit}
          gaListName={productListName}
        >
          {name}
        </ProductLink>
        {shortDescription && (
          <HeightLimitedExpander
            containsHtml
            id="customizeItemExpander"
            lessButtonText={t('VIEW_LESS')}
            maxHeight={80}
            moreButtonText={t('VIEW_MORE_PRODUCTS')}
            className="u-fs--14"
          >
            {shortDescription}
          </HeightLimitedExpander>
        )}
        {isSM &&
          getProp65Information(
            prop65,
            t('INACTIVITY_WARNING_DIALOG_WARNING_ICON')
          )}
        {!isSM && (
          <>
            <div className="mt-2">
              <div className="mb-2">
                <span className="text-univers--bold u-fs--16 d-inline">
                  {getPriceInformation(item)}
                </span>
                <span className="u-fs--16 d-inline">
                  {price ? ` (${currencyCode})` : ''}
                </span>
              </div>
              {getQuantityRenderer(item)}
            </div>
            <div
              className="mt-2 mb-3 d-flex"
              style={{ height: '40px', gap: '16px' }}
            >
              <AlloyButton
                buttonType="secondary"
                disabled={isLoading || isInDisabledList || disabledAddToCart}
                onClick={() => addToCartHandler(partNumber)}
              >
                {t('ADD_TO_CART')}
              </AlloyButton>
              {getItemsInCartRenderer(item)}
            </div>
            <div className="mb-2 ms-2 ps-2">
              {getAvailabilityInformation(item)}
            </div>
            {getProp65Information(
              prop65,
              t('INACTIVITY_WARNING_DIALOG_WARNING_ICON')
            )}
          </>
        )}
      </>
    );
  };

  const customRenderers = {
    quantity: getQuantityRenderer,
    thumbnail: itemImageRenderer,
    name: getProductInformation,
    price: getPriceInformation,
    availability: getAvailabilityInformation,
    addtocart: addToCartRenderer,
    carticon: getItemsInCartRenderer
  };
  const gaInfo = (currentItem, product) => {
    return {
      brand: 'CAT',
      businessUnit: 'PCC',
      kitId: currentKit?.uniqueId,
      packageFlag: currentKit?.type === PACKAGE_BEAN ? 'Yes' : 'No',
      relatedModel: model,
      category: currentItem.categoryPath,
      id: currentItem.partNumber,
      name: product?.name,
      price: convertFormattedNumberToUnformatted(currentItem.totalPrice || ''),
      productWeight: currentItem.weight,
      quantity: currentItem.quantity
    };
  };

  // * Get the items on for the cart *
  const getCartItems = ({ itemsArray, partNumber, productsArray }) => {
    let cartItems;
    let products = [];

    if (partNumber) {
      const productItem = productsArray?.find(
        product => product.partNumber === partNumber
      );
      const productPartNumber = productItem.partNumber;
      const isInPriceAndAvailability = itemsArray.includes(productPartNumber);
      let quantity = isInPriceAndAvailability
        ? priceAndAvailability?.[partNumber]?.quantity
        : productItem.quantity;
      cartItems = [
        {
          partNumber: productPartNumber,
          quantity: String(quantity)
        }
      ];
      // Google Analytics events
      const currentProduct = priceAndAvailability?.[partNumber];
      const checkFitment = getFitmentCheck(currentProduct?.partNumber);
      const info = {
        ...gaInfo(currentProduct, productItem),
        checkFitment
      };
      products = [info];
    } else {
      // * This reduce is to filter items that were changed to 0 quantity and 0 price
      cartItems = productsArray?.reduce((restOfItems, productItem) => {
        const productPartNumber = productItem.partNumber;
        const isInPriceAndAvailability = itemsArray.includes(productPartNumber);
        let quantity = isInPriceAndAvailability
          ? priceAndAvailability?.[productPartNumber]?.quantity
          : productItem.quantity;
        if (quantity !== 0 && isInPriceAndAvailability) {
          return [
            ...restOfItems,
            {
              partNumber: productPartNumber,
              quantity: String(quantity)
            }
          ];
        } else {
          return restOfItems;
        }
      }, []);
      // Google Analytics events
      products = productsArray?.map((product, index) => {
        const currentProduct = priceAndAvailability?.[product.partNumber];
        const checkFitment = getFitmentCheck(currentProduct?.partNumber);
        return {
          ...gaInfo(currentProduct, product),
          checkFitment,
          position: index + 1
        };
      });
    }
    if (gaListName) {
      fireProductClickEvent({ list: gaListName, products, dealerStore });
    }

    fireAddToCartEvent({
      products,
      totalPrice: convertFormattedNumberToUnformatted(subTotal || ''),
      dealerStore
    });

    return cartItems;
  };

  // * Adds items to cart, if partNumber is provided, then only the selected item
  // * will be added to cart
  const addToCartHandler = async (partNumber = null) => {
    // * This array only has the id for each component
    // * filter the items that is not the current item (main sku leve item)
    // * and has zero price and doesn't have the price attribute
    const itemsArray = Object.keys(priceAndAvailability)?.filter(
      itemPartNumber =>
        itemPartNumber !== currentKitPartNumber &&
        priceAndAvailability[itemPartNumber]?.totalPrice &&
        priceAndAvailability[itemPartNumber]?.totalPrice !== formattedZeroPrice
    );

    const isBundle = BUNDLE_BEAN === currentKit.type;

    let itemSosCode = isBundle
      ? priceAndAvailability[itemsArray[0]]?.sosCode
      : sosCode;

    // * This array has the whole object of each component
    const productsArray = productItems?.filter(
      item => item.uniqueId !== current
    );
    let cartItems = getCartItems({ itemsArray, partNumber, productsArray });

    if (currentKit?.type === PACKAGE_BEAN) {
      const kitPartNumberList = cartItems
        .map(cartItems => cartItems.partNumber)
        .join('~');
      setCookie('customKit', currentKit?.partNumber + '|' + kitPartNumberList, {
        path: '/',
        expires: 1
      });
    }

    try {
      await addItemsToCart(
        cartItems,
        {
          x_calculateOrder: itemSosCode ?? ''
        },
        true
      );
      if (partNumber) {
        ctx.createSuccessToast(<p>{t('ADDED_PART')}</p>);
      } else {
        ctx.createSuccessToast(<AddToCartToast />);
      }
      dispatch(clearError(ERROR_DOMAIN.PDP, ERROR_PATH.MAIN));
    } catch (error) {
      const err = normalizeError(error);
      dispatch(setError(ERROR_DOMAIN.PDP, ERROR_PATH.MAIN, error));
      ctx.createErrorToast(`${err}`);
    } finally {
      if (!partNumber) {
        closeModal();
      }
    }
  };
  const getSubTotalSection = () => {
    const showSubtotalError = itemsHaveInvalidQuantity || !isInstantAccess;
    return (
      <div className="d-flex justify-content-end color-extra-dark-gray u-fs--16 pt-3">
        <p className="me-1 mb-0 text-univers--bold text-uppercase">
          {t('CUSTOMIZE_MODAL_SUBTOTAL')}
        </p>
        {itemsAreLoading || subtotalLoading || showSubTotalLoader ? (
          <InlineLoader />
        ) : (
          <span
            className={`${
              showSubtotalError ? styles['subtotal-error'] : 'fw-bold '
            }`}
          >
            {showSubtotalError ? (
              t('PD_PRICE_UNAVAILABLE')
            ) : (
              <>
                {subTotal}
                <span className="ms-1">{currencyCode}</span>
              </>
            )}
          </span>
        )}
      </div>
    );
  };

  return (
    <>
      {loadingAddItemsToCart && (
        <ComponentLoader contentClassName="position-sticky" />
      )}
      <Table
        schema={itemsIncludedSchema}
        data={items || []}
        customRenderers={customRenderers}
      />
      <StickySection
        className={cx('bg-white px-3', styles['modal__sticky-container'])}
      >
        {getSubTotalSection()}
        <div className="d-flex flex-column d-md-block pt-3">
          <AlloyButton
            buttonType="primary"
            size="dynamic"
            onClick={() => addToCartHandler()}
            className="float-md-end"
            disabled={disableAddSelectionToCart}
          >
            {t('KITS_ADD_SELECTION_CUSTOM')}
          </AlloyButton>
          <AlloyButton buttonType="ghost" size="dynamic" onClick={closeModal}>
            {t('CLOSE')}
          </AlloyButton>
        </div>
      </StickySection>
    </>
  );
};

CustomizeModalTable.propTypes = {
  items: PropTypes.array,
  closeModal: PropTypes.func,
  addItemsToCart: PropTypes.func,
  loadingAddItemsToCart: PropTypes.bool,
  gaListName: PropTypes.array
};
export default CustomizeModalTable;
